import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { Select } from '../../../../../components/forms/Select';
import { MoneyInput } from '../../../../../components/forms/MoneyInput';
import { Button } from '../../../../../components/Button';
import { formatMoney } from '../../../../../utils/formatMoney';
import { CREDIT_PURPOSE_OPTIONS, INCOME_TYPE_OPTIONS } from '../../constants';
import { incomeStepSchema, IncomeStepValues } from '../../schemas';
import { WizardShell } from '../WizardShell/WizardShell';
import styles from '../WizardShell/WizardShell.module.css';

interface IIncomeStep {
  isLegalPerson: boolean;
  necessaryIncome?: number;
  defaultValues: Partial<IncomeStepValues>;
  onBack: () => void;
  onSubmit: (values: IncomeStepValues) => void;
}

export function IncomeStep({
  isLegalPerson,
  necessaryIncome,
  defaultValues,
  onBack,
  onSubmit,
}: IIncomeStep) {
  const {
    register,
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<IncomeStepValues>({
    resolver: zodResolver(incomeStepSchema),
    mode: 'onBlur',
    defaultValues: {
      incomeType: '',
      creditPurpose: '',
      ...defaultValues,
    },
  });

  const monthlyIncome = watch('monthlyIncome');
  const belowNecessary =
    necessaryIncome !== undefined &&
    monthlyIncome !== undefined &&
    monthlyIncome < necessaryIncome;

  return (
    <WizardShell
      step={3}
      totalSteps={4}
      title="Renda e finalidade"
      nextTitle="Revisão"
      helper="Falta pouco. Essas informações ajudam a CashMe a avaliar a sua proposta."
    >
      <form onSubmit={handleSubmit(onSubmit)} noValidate>
        <div className={styles.field}>
          <Controller
            control={control}
            name="monthlyIncome"
            render={({ field }) => (
              <MoneyInput
                name="monthlyIncome"
                label={
                  isLegalPerson
                    ? 'Faturamento mensal'
                    : 'Qual a sua renda mensal?'
                }
                placeholder="R$ 4.000"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                errors={errors.monthlyIncome?.message}
              />
            )}
          />
          {belowNecessary && !errors.monthlyIncome && (
            <p
              style={{
                fontSize: 12,
                color: '#ca8a04',
              }}
            >
              A renda considerada para este crédito é de{' '}
              {formatMoney(necessaryIncome)}. Você pode seguir, mas a CashMe
              pode apontar isso na análise.
            </p>
          )}
        </div>

        {!isLegalPerson && (
          <div className={styles.field}>
            <Select
              label="Tipo de renda (opcional)"
              placeholder="Selecione"
              options={INCOME_TYPE_OPTIONS}
              errors={errors.incomeType?.message}
              {...register('incomeType')}
            />
          </div>
        )}

        <div className={styles.field}>
          <Select
            label="Para que você vai usar o crédito?"
            placeholder="Selecione"
            options={CREDIT_PURPOSE_OPTIONS}
            errors={errors.creditPurpose?.message}
            {...register('creditPurpose')}
          />
        </div>

        <div className={styles.footer}>
          <Button type="button" color="flat" text="Voltar" onClick={onBack} />
          <Button type="submit" color="primary" text="Continuar" />
        </div>
      </form>
    </WizardShell>
  );
}
